import { Component, Input, OnDestroy, OnInit, inject, WritableSignal, signal } from '@angular/core';
import { InvoiceApprovalInfo } from '../../types/InvoiceApprovalInfo';
import { InvoiceDataService } from '../../invoice-data.service';
import { TranslateModule } from '@ngx-translate/core';
import { PersonModule } from '@lib/user';

import { MatDialog } from '@angular/material/dialog';
import { DialogOptions } from '@lib/util';
import { InvoiceApproverOverviewDialogComponent } from './dialog/invoice-approver-overview-dialog/invoice-approver-overview-dialog.component';
import { InvoiceStatus } from '@lib/invoice-public';
import { Subscription, interval, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { IimSpinnerModule } from '@iim-angular/components';

@Component({
    selector: 'invoice-approver-overview',
    templateUrl: './invoice-approver-overview.component.html',
    styleUrls: ['./invoice-approver-overview.component.scss'],
    imports: [TranslateModule, PersonModule, IimSpinnerModule],
    standalone: true,
})
export class InvoiceApproverOverviewComponent implements OnInit, OnDestroy {
    private readonly dataService = inject(InvoiceDataService);
    private readonly dialog = inject(MatDialog);

    @Input() public invoiceId!: string;
    @Input() public invoiceStatus?: InvoiceStatus;

    public approverInfo?: InvoiceApprovalInfo;
    public isLoadingApproverInfo: WritableSignal<boolean> = signal(false);

    private _pollSub?: Subscription;

    public getData(): void {
        this.dataService.getApprovalInfoFor(this.invoiceId).subscribe((info: InvoiceApprovalInfo) => {
            this.approverInfo = info;
            this._startPollingIfNeeded();
        });
    }

    private _startPollingIfNeeded(): void {
        const isUnapproved = this.invoiceStatus === InvoiceStatus.UNAPPROVED;
        const hasNoApproverInfo = !this.approverInfo || this.approverInfo.groups.length === 0;

        if (!isUnapproved || !hasNoApproverInfo) {
            this.isLoadingApproverInfo.set(false);
            this._stopPolling();
            return;
        }

        if (this._pollSub && !this._pollSub.closed) {
            // already polling
            this.isLoadingApproverInfo.set(true);
            return;
        }

        this.isLoadingApproverInfo.set(true);
        this._pollSub = interval(1000).subscribe(() => {
            if (this.invoiceStatus === InvoiceStatus.UNAPPROVED) {
                this.dataService
                    .getApprovalInfoFor(this.invoiceId)
                    .pipe(catchError(() => of(this.approverInfo as InvoiceApprovalInfo))) // keep polling on error
                    .subscribe((info: InvoiceApprovalInfo | undefined) => {
                        if (info) {
                            this.approverInfo = info;
                            const found = this.approverInfo.groups && this.approverInfo.groups.length > 0;
                            if (found) {
                                this._stopPolling();
                                this.isLoadingApproverInfo.set(false);
                            }
                        }
                    });
            } else {
                this._stopPolling();
                this.isLoadingApproverInfo.set(false);
            }
        });
    }

    private _stopPolling(): void {
        if (this._pollSub) {
            this._pollSub.unsubscribe();
            this._pollSub = undefined;
        }
    }

    public openDetails(): void {
        if (this.approverInfo) {
            this.dialog.open(InvoiceApproverOverviewDialogComponent, DialogOptions.SMALL(this.approverInfo));
        }
    }

    ngOnInit(): void {
        this.getData();
    }

    ngOnDestroy(): void {
        this._stopPolling();
        this.isLoadingApproverInfo.set(false);
    }
}
